import { useMemo, useState } from "react";
import {
  Box, Stack, Typography, Paper, Alert, AlertTitle, Button, TextField, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  CircularProgress, Accordion, AccordionSummary, AccordionDetails, Chip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { modificarCvu, type ModificarCvuDatos, type ModificarCvuResult } from "../../services/cmf";

const MONO = '"SF Mono","JetBrains Mono",ui-monospace,SFMono-Regular,Menlo,monospace';

// Spec Coelsa "Modificar cuenta CVU" (docs/coelsa-cvu-modificar.md): tipo y moneda
// tienen un único valor válido; el éxito es respuesta.codigo 3200 (estado final "CVU ACTIVO",
// lo que devuelve prd) o 3300 ("modificado correctamente", el de la tabla).
const TIPO_FIJO = "1" as const;
const MONEDA_FIJA = "032" as const;
const CODIGOS_OK = new Set(["3200", "3300"]);
const PERSONAS = [
  { value: "J", label: "J · Jurídica" },
  { value: "F", label: "F · Física" },
];

type Form = { cvu: string; cuit: string; cta_id: string; titular: string; persona_tipo: "F" | "J" };
const INITIAL: Form = { cvu: "", cuit: "", cta_id: "", titular: "", persona_tipo: "J" };

// Espejo de la validación del backend (_modificar_cvu_validar): sirve para no
// mandar algo que va a rebotar, pero la verdad la tiene el backend.
function errores(f: Form): Partial<Record<keyof Form, string>> {
  const e: Partial<Record<keyof Form, string>> = {};
  if (!/^\d{22}$/.test(f.cvu.trim())) e.cvu = "22 dígitos";
  if (!/^\d{11}$/.test(f.cuit.trim().replace(/-/g, ""))) e.cuit = "11 dígitos (con o sin guiones)";
  if (!/^\d+$/.test(f.cta_id.trim())) e.cta_id = "entero ≥ 0";
  if (!f.titular.trim()) e.titular = "obligatorio";
  return e;
}

function toDatos(f: Form): ModificarCvuDatos {
  return {
    cvu: f.cvu.trim(),
    cuit: f.cuit.trim().replace(/-/g, ""),
    cta_id: parseInt(f.cta_id.trim(), 10),
    tipo: TIPO_FIJO,
    titular: f.titular.trim().replace(/\s+/g, " "),
    moneda: MONEDA_FIJA,
    persona_tipo: f.persona_tipo,
  };
}

export default function CmfModificarCvu() {
  const [form, setForm] = useState<Form>(INITIAL);
  const [confirm, setConfirm] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<ModificarCvuResult | null>(null);

  const errs = useMemo(() => errores(form), [form]);
  const valido = Object.keys(errs).length === 0;
  const datos = useMemo(() => (valido ? toDatos(form) : null), [form, valido]);
  // Lo que ve el operador en el diálogo es EXACTAMENTE lo que el backend manda al
  // conector (el backend sólo agrega el wrapper {"banco","datos"}).
  const previewJson = useMemo(
    () => JSON.stringify({ banco: "CMF", datos: datos ?? {} }, null, 2),
    [datos],
  );

  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const enviar = async () => {
    if (!datos) return;
    setEnviando(true); setErr(null);
    try {
      const r = await modificarCvu(datos);
      setResult(r);
    } catch (e: any) {
      setErr(e?.message ?? "Error al modificar la CVU");
    } finally {
      setEnviando(false);
      setConfirm(false);
    }
  };

  return (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>Modificar CVU en Coelsa</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 900 }}>
        Cambia los datos que Coelsa tiene registrados para una CVU (titular, tipo de persona y cuenta
        recaudadora). Se cursa contra el conector de <strong>producción</strong> (CoelsaConnectorProd,
        operación ModificarCvu) y queda auditado con tu usuario. La CVU y el CUIT identifican qué CVU
        se modifica; el resto son los valores nuevos. No hay deshacer: si te equivocás, volvé a
        modificar con los valores correctos. Spec y códigos: <code>docs/coelsa-cvu-modificar.md</code>.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, maxWidth: 900 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField label="CVU" value={form.cvu} onChange={set("cvu")} size="small" required
                       error={!!form.cvu && !!errs.cvu} helperText={form.cvu && errs.cvu ? errs.cvu : "22 dígitos. Identifica la CVU a modificar."}
                       slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 22, style: { fontFamily: MONO } } }}
                       sx={{ minWidth: 300 }} />
            <TextField label="CUIT del titular" value={form.cuit} onChange={set("cuit")} size="small" required
                       error={!!form.cuit && !!errs.cuit} helperText={form.cuit && errs.cuit ? errs.cuit : "11 dígitos. Tiene que ser el CUIT con el que la CVU está registrada."}
                       slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 13, style: { fontFamily: MONO } } }}
                       sx={{ minWidth: 260 }} />
            <TextField label="cta_id" value={form.cta_id} onChange={set("cta_id")} size="small" required
                       error={!!form.cta_id && !!errs.cta_id} helperText={form.cta_id && errs.cta_id ? errs.cta_id : "ID de la cuenta recaudadora en Coelsa (ej. 2)."}
                       slotProps={{ htmlInput: { inputMode: "numeric", style: { fontFamily: MONO } } }}
                       sx={{ width: 220 }} />
          </Stack>
          <TextField label="Titular" value={form.titular} onChange={set("titular")} size="small" required fullWidth
                     error={!!form.titular && !!errs.titular}
                     helperText="Razón social o nombre tal como debe quedar registrado en Coelsa." />
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField select label="Tipo de persona" value={form.persona_tipo} onChange={set("persona_tipo")} size="small"
                       helperText="J = jurídica (empresa), F = física." sx={{ minWidth: 220 }}>
              {PERSONAS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            <TextField label="Tipo de CVU" value={`${TIPO_FIJO} (único valor válido)`} size="small"
                       helperText="Fijo según spec Coelsa." sx={{ minWidth: 220 }}
                       slotProps={{ input: { readOnly: true } }} />
            <TextField label="Moneda" value={`${MONEDA_FIJA} (pesos, único valor válido)`} size="small"
                       helperText="Fijo según spec Coelsa." sx={{ minWidth: 260 }}
                       slotProps={{ input: { readOnly: true } }} />
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button variant="contained" color="warning" startIcon={<EditIcon />}
                    disabled={!valido || enviando} onClick={() => setConfirm(true)}>
              Modificar CVU
            </Button>
            <Button variant="text" disabled={enviando} onClick={() => { setForm(INITIAL); setResult(null); setErr(null); }}>
              Limpiar
            </Button>
            {!valido && (
              <Typography variant="caption" color="text.secondary">
                Completá CVU, CUIT, cta_id y titular para habilitar el envío.
              </Typography>
            )}
          </Stack>
        </Stack>
      </Paper>

      {err && <Alert severity="error" sx={{ mb: 2, maxWidth: 900 }}>{err}</Alert>}

      {result && (
        <Alert severity={result.ok ? "success" : "error"} sx={{ mb: 2, maxWidth: 900 }}>
          <AlertTitle>
            {result.ok ? "Coelsa aceptó la modificación" : "La modificación NO se aplicó"}
            {" "}
            <Chip size="small" variant="outlined" label={`HTTP ${result.status}`} />
            {result.codigo && (
              <Chip size="small" sx={{ ml: 0.5 }} color={CODIGOS_OK.has(result.codigo) ? "success" : "error"}
                    label={`Coelsa ${result.codigo}`} />
            )}
          </AlertTitle>
          <Typography variant="body2">
            {result.codigo ? (result.descripcion || result.resumen) : result.resumen}
          </Typography>
          {!result.ok && !result.codigo && result.status >= 500 && (
            <Typography variant="caption" color="text.secondary">
              Cuando Coelsa rechaza (400/403/409), el conector devuelve 500 y no reenvía el motivo: está en el
              log de CoelsaConnectorProd (línea "Rta Coelsa").
            </Typography>
          )}
          <Accordion variant="outlined" sx={{ mt: 1, bgcolor: "transparent" }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="caption">Ver JSON enviado y respuesta cruda</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>Enviado al conector</Typography>
              <pre style={{ fontFamily: MONO, fontSize: 12, margin: "4px 0 12px", whiteSpace: "pre-wrap" }}>
                {JSON.stringify(result.enviado, null, 2)}
              </pre>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>Respuesta</Typography>
              <pre style={{ fontFamily: MONO, fontSize: 12, margin: "4px 0 0", whiteSpace: "pre-wrap" }}>
                {typeof result.respuesta === "string" ? result.respuesta : JSON.stringify(result.respuesta, null, 2)}
              </pre>
            </AccordionDetails>
          </Accordion>
        </Alert>
      )}

      <Dialog open={confirm} onClose={() => !enviando && setConfirm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Modificar CVU en Coelsa (producción)</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            Se va a enviar exactamente este cuerpo al conector. Revisá CVU y CUIT: si no coinciden con
            lo registrado, Coelsa lo rechaza (3310); si coinciden, los demás valores reemplazan a los actuales.
          </DialogContentText>
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            Según la spec de Coelsa, <strong>la modificación reactiva la cuenta CVU si estaba eliminada</strong>.
            Si esta CVU fue dada de baja, después de esta operación vuelve a estar activa.
          </Alert>
          <pre style={{ fontFamily: MONO, fontSize: 12, margin: 0, whiteSpace: "pre-wrap",
                        background: "rgba(0,0,0,0.04)", padding: 12, borderRadius: 6 }}>
            {previewJson}
          </pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)} disabled={enviando}>Cancelar</Button>
          <Button onClick={enviar} variant="contained" color="warning" disabled={enviando}
                  startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : <EditIcon />}>
            {enviando ? "Enviando…" : "Confirmar modificación"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
